import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, TextField, IconButton, Typography, Paper, CircularProgress, Tooltip, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip, Divider } from '@mui/material';
import { Send, DoneAll, MoreVert, AttachFile, Reply, Edit, Delete, Image, Description, GetApp, AlternateEmail, Close } from '@mui/icons-material';
import VideoCall from './VideoCall';
import { getMessages, sendMessage, subscribeToMessages, unsubscribeFromMessages, markConversationRead, renameConversation, leaveConversation, addParticipants, uploadFile, deleteMessage, editMessage, addReaction, removeReaction } from '../../../../api/chat';
import { GetOrgUsers, GetStudy } from '../../../../api';
import StudyTagPopover from './StudyTagPopover';

const drafts = {};

function msgDateLabel(dateStr) {
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const msgDay = new Date(date);
  msgDay.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today - msgDay) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'long' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function msgDayKey(dateStr) {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

export default function ChatWindow({ user, conversationId, conversation, onUpdate, onLeave, access, socketStatus, onClose }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [renameDialog, setRenameDialog] = useState(false);
  const [newName, setNewName] = useState('');
  const [addMembersDialog, setAddMembersDialog] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [studySearch, setStudySearch] = useState('');
  const [studyOptions, setStudyOptions] = useState([]);
  const [selectedStudies, setSelectedStudies] = useState([]);
  const [studyPopover, setStudyPopover] = useState(null);
  const searchTimeoutRef = useRef(null);
  const [studySearchLoading, setStudySearchLoading] = useState(false);
  const typingTimeoutRef = useRef(null);
  const [typingUsers, setTypingUsers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [messageMenuAnchor, setMessageMenuAnchor] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [reactionMenuAnchor, setReactionMenuAnchor] = useState(null);
  const [transcriptDialog, setTranscriptDialog] = useState(null);

  const fileInputRef = useRef(null);
  
  const REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '😡'];

  const abortControllerRef = useRef(null);
  const prevSocketStatus = useRef(socketStatus);

  const loadMessages = useCallback(async (pageNum) => {
    if (!conversationId) return;
    
    // Only cancel if we're loading page 0 (new conversation)
    if (pageNum === 0 && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for page 0 only
    if (pageNum === 0) {
      abortControllerRef.current = new AbortController();
    }
    
    const currentController = abortControllerRef.current;
    
    setLoading(prev => {
      if (prev && pageNum > 0) return prev; // Don't load if already loading pagination
      return true;
    });
    
    try {
      const data = await getMessages(conversationId, 20, pageNum * 20, currentController?.signal);
      
      // Check if request was aborted
      if (currentController?.signal?.aborted) return;
      
      const messages = data || [];
      if (messages.length < 20) setHasMore(false);
      setMessages(prev => pageNum === 0 ? messages : [...messages, ...prev]);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return; // Ignore aborted requests
      console.error('Failed to load messages:', err);
      if (pageNum === 0) setMessages([]);
    }
    setLoading(false);
  }, [conversationId]);

  const handleNewMessage = useCallback((data) => {
    if (data.type === 'insert') {
      loadMessages(0);
      // Auto-mark as read if conversation is currently open
      if (conversationId) {
        setTimeout(() => {
          markConversationRead(conversationId).catch(() => {});
        }, 500);
      }
    }
  }, [loadMessages, conversationId]);

  useEffect(() => {
    if (!conversationId) return;
    
    // Cancel any pending requests when conversation changes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    setMessages([]);
    setPage(0);
    setHasMore(true);
    setTypingUsers([]);
    setNewMessage(drafts[conversationId] || '');
    loadMessages(0);
    markConversationRead(conversationId).catch(() => {});

    subscribeToMessages(conversationId, handleNewMessage);
    
    const { socket } = require('../../../../api');
    if (socket) {
      const typingHandler = (data) => {
        if (data.conversationId === conversationId && String(data.userId) !== String(user?.id)) {
          setTypingUsers(prev => {
            if (data.isTyping) {
              return prev.some(u => u.id === data.userId) ? prev : [...prev, { id: data.userId, name: data.userName }];
            } else {
              return prev.filter(u => u.id !== data.userId);
            }
          });
        }
      };
      
      const readHandler = (data) => {
        if (data.conversationId === conversationId) {
          loadMessages(0); // Refresh messages to show updated read status
        }
      };
      
      const reactionHandler = (data) => {
        loadMessages(0); // Refresh messages to show updated reactions
      };

      const messageDeletedHandler = (data) => {
        if (data.conversationId === conversationId) {
          setMessages(prev => prev.filter(m => m.id !== data.messageId));
        }
      };

      const messageEditedHandler = (data) => {
        if (data.conversationId === conversationId) {
          setMessages(prev => prev.map(m => m.id === data.messageId ? { ...m, content: data.content, edited: true } : m));
        }
      };

      socket.on('typing', typingHandler);
      socket.on('messageRead', readHandler);
      socket.on('reactionAdded', reactionHandler);
      socket.on('reactionRemoved', reactionHandler);
      socket.on('messageDeleted', messageDeletedHandler);
      socket.on('messageEdited', messageEditedHandler);
      
      return () => {
        unsubscribeFromMessages(conversationId);
        socket.off('typing', typingHandler);
        socket.off('messageRead', readHandler);
        socket.off('reactionAdded', reactionHandler);
        socket.off('reactionRemoved', reactionHandler);
        socket.off('messageDeleted', messageDeletedHandler);
        socket.off('messageEdited', messageEditedHandler);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          socket.emit('typing', { conversationId, userId: user?.id, isTyping: false });
        }
      };
    }
    
    return () => {
      // Cancel pending requests on cleanup
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      unsubscribeFromMessages(conversationId);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        const { socket } = require('../../../../api');
        if (socket) socket.emit('typing', { conversationId, userId: user?.id, isTyping: false });
      }
    };
  }, [conversationId, handleNewMessage, loadMessages, user?.id]);

  // Refresh messages and re-establish socket listeners when socket reconnects
  useEffect(() => {
    if (socketStatus && !prevSocketStatus.current && conversationId) {
      loadMessages(0);
      
      // Re-establish socket event listeners
      const { socket } = require('../../../../api');
      if (socket) {
        const typingHandler = (data) => {
          if (data.conversationId === conversationId && String(data.userId) !== String(user?.id)) {
            setTypingUsers(prev => {
              if (data.isTyping) {
                return prev.some(u => u.id === data.userId) ? prev : [...prev, { id: data.userId, name: data.userName }];
              } else {
                return prev.filter(u => u.id !== data.userId);
              }
            });
          }
        };
        
        socket.on('typing', typingHandler);
      }
    }
    prevSocketStatus.current = socketStatus;
  }, [socketStatus, conversationId, loadMessages, user?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() && !attachments.length) return;
    
    const content = newMessage;
    const studyTags = selectedStudies.map(s => ({
      studyId: s.id,
      patientId: s.PatientMainDicomTags?.PatientID,
      patientName: s.PatientMainDicomTags?.PatientName
    }));
    

    
    setNewMessage('');
    setSelectedStudies([]);
    setAttachments([]);
    setReplyTo(null);
    if (conversationId) delete drafts[conversationId];
    
    try {
      if (editingMessage) {
        await editMessage(editingMessage.id, content);
        setEditingMessage(null);
        loadMessages(0);
      } else {
        await sendMessage(conversationId, content, studyTags, attachments);
      }
    } catch (err) {
      console.error('Failed to send message', err);
      alert('Failed to send message. Please try again.');
      setNewMessage(content);
      setSelectedStudies(studyTags.map(t => ({ id: t.studyId })));
      setAttachments(attachments);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    const uploadPromises = files.map(async (file) => {
      try {
        if (file.size > 10 * 1024 * 1024) {
          alert(`File ${file.name} is too large. Maximum size is 10MB.`);
          return null;
        }
        const result = await uploadFile(file);
        return result;
      } catch (err) {
        console.error('Failed to upload file:', err);
        alert(`Failed to upload ${file.name}. Please try again.`);
        return null;
      }
    });
    
    const uploadedFiles = (await Promise.all(uploadPromises)).filter(Boolean);
    if (uploadedFiles.length > 0) {
      setAttachments(prev => [...prev, ...uploadedFiles]);
    }
    
    // Clear the input
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await deleteMessage(messageId);
      loadMessages(0);
      setMessageMenuAnchor(null);
    } catch (err) {
      console.error('Failed to delete message:', err);
    }
  };

  const handleEditMessage = (message) => {
    setEditingMessage(message);
    setNewMessage(message.content);
    setMessageMenuAnchor(null);
  };

  const handleReply = (message) => {
    setReplyTo(message);
    setMessageMenuAnchor(null);
  };

  const handleReaction = async (messageId, emoji) => {
    try {
      await addReaction(messageId, emoji);
      loadMessages(0);
      setReactionMenuAnchor(null);
    } catch (err) {
      console.error('Failed to add reaction:', err);
    }
  };

  const handleRemoveReaction = async (messageId, emoji) => {
    try {
      await removeReaction(messageId, emoji);
      loadMessages(0);
    } catch (err) {
      console.error('Failed to remove reaction:', err);
    }
  };

  const handleScroll = (e) => {
    if (e.target.scrollTop === 0 && hasMore && !loading) {
      const newPage = page + 1;
      setPage(newPage);
      loadMessages(newPage);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleTyping = useCallback(() => {
    const { socket } = require('../../../../api');
    if (socket) {
      socket.emit('typing', { conversationId, userId: user?.id, userName: user?.name, isTyping: true });
      
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit('typing', { conversationId, userId: user?.id, userName: user?.name, isTyping: false });
      }, 3000);
    }
  }, [conversationId, user?.id, user?.name]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        const { socket } = require('../../../../api');
        if (socket) socket.emit('typing', { conversationId, userId: user?.id, userName: user?.name, isTyping: false });
      }
    }
  };

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    if (studySearch.length > 2 && access?.org?.id) {
      setStudySearchLoading(true);
      searchTimeoutRef.current = setTimeout(() => {
        GetStudy(null, { 
          view: 'clinic',
          org: access.org.id, 
          search: { query: studySearch },
          limit: 10,
          order: 'desc',
          sort: 'createdAt'
        }).then(studies => {
          setStudyOptions(studies || []);
          setStudySearchLoading(false);
        }).catch(() => {
          setStudyOptions([]);
          setStudySearchLoading(false);
        });
      }, 300);
    } else {
      setStudyOptions([]);
      setStudySearchLoading(false);
    }
    
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [studySearch, access]);

  const handleRename = async () => {
    if (newName.trim()) {
      try {
        await renameConversation(conversationId, newName.trim());
        setRenameDialog(false);
        setNewName('');
        onUpdate?.();
      } catch (err) {
        console.error('Failed to rename conversation', err);
        alert('Failed to rename conversation. Please try again.');
      }
    }
  };

  const handleLeave = async () => {
    try {
      await leaveConversation(conversationId);
      setMenuAnchor(null);
      onLeave?.();
    } catch (err) {
      console.error('Failed to leave conversation', err);
      alert('Failed to leave conversation. Please try again.');
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const allUsers = await GetOrgUsers(access?.org?.id);
      const participantIds = conversation?.participants?.map(p => p.id) || [];
      setAvailableUsers(allUsers.filter(u => !participantIds.includes(u.id)));
    } catch (err) {
      console.error('Failed to load users', err);
    }
  };

  const handleAddMembers = async () => {
    if (selectedUsers.length > 0) {
      try {
        await addParticipants(conversationId, selectedUsers.map(u => u.id));
        setAddMembersDialog(false);
        setSelectedUsers([]);
        onUpdate?.();
      } catch (err) {
        console.error('Failed to add members', err);
        alert('Failed to add members. Please try again.');
      }
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      minHeight: 0,
      position: 'relative'
    }}>
      {/* Mobile back/close bar */}
      <Box sx={{
        display: { xs: 'flex', sm: 'none' },
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 1,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        flexShrink: 0
      }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 500 }}>
          {conversation?.isGroup ? (conversation.groupName || 'Group Chat') : 
           conversation?.participants?.find(p => p.id !== user?.id)?.name || 'Chat'}
        </Typography>
        <IconButton 
          onClick={onClose || onLeave} 
          sx={{ color: 'inherit' }}
          size="small"
        >
          <Close />
        </IconButton>
      </Box>
      {conversation?.isGroup && (
        <Box sx={{ 
          p: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          display: { xs: 'none', sm: 'block' },
          flexShrink: 0
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">
              {conversation.groupName || 'Group Chat'}
            </Typography>
            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVert />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              <MenuItem onClick={() => { setMenuAnchor(null); setRenameDialog(true); }}>Rename</MenuItem>
              <MenuItem onClick={() => { setMenuAnchor(null); loadAvailableUsers(); setAddMembersDialog(true); }}>Add Members</MenuItem>
              <MenuItem onClick={handleLeave}>Leave Conversation</MenuItem>
            </Menu>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {conversation.participants?.filter(p => p.id !== user?.id).map(p => p.name).join(', ')}
          </Typography>
        </Box>
      )}
      <Box 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        sx={{ 
          flex: 1, 
          overflow: 'auto', 
          p: { xs: 1, sm: 2 },
          display: 'flex', 
          flexDirection: 'column',
          minHeight: 0
        }}
      >
        {loading && page > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {messages.map((msg, idx) => {
          const isOwn = msg.senderId === user?.id;
          const showDivider = idx === 0 || msgDayKey(msg.createdAt) !== msgDayKey(messages[idx - 1].createdAt);
          return (
            <Box key={msg.id || idx}>
            {showDivider && (
              <Box sx={{ display: 'flex', alignItems: 'center', my: 1.5 }}>
                <Divider sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ mx: 1.5, whiteSpace: 'nowrap' }}>
                  {msgDateLabel(msg.createdAt)}
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: isOwn ? 'flex-end' : 'flex-start',
                mb: 1
              }}
            >
              <Paper
                sx={{
                  p: { xs: 1, sm: 1.5 },
                  maxWidth: { xs: '85%', sm: '70%' },
                  bgcolor: isOwn ? 'primary.main' : 'action.hover',
                  color: isOwn ? 'primary.contrastText' : 'text.primary',
                  borderRadius: 2,
                  position: 'relative'
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setSelectedMessage(msg);
                  setMessageMenuAnchor(e.currentTarget);
                }}
              >
                {!isOwn && (
                  <Typography variant="caption" fontWeight="bold" display="block">
                    {msg.senderName}
                  </Typography>
                )}
                {msg.replyTo && (
                  <Box sx={{ p: 1, mb: 1, bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Replying to {msg.replyTo.senderName}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {msg.replyTo.content?.substring(0, 50)}{msg.replyTo.content?.length > 50 ? '...' : ''}
                    </Typography>
                  </Box>
                )}
                {msg.content && <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>}
                {msg.attachments?.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {msg.attachments.map((attachment, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 1, mb: 0.5 }}>
                        {attachment.type?.startsWith('image/') ? <Image /> : <Description />}
                        <Typography variant="body2" sx={{ flex: 1 }}>{attachment.name}</Typography>
                        <IconButton size="small" onClick={() => window.open(attachment.url, '_blank')}>
                          <GetApp fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
                {msg.studyTags?.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                    {msg.studyTags.map((tag, idx) => (
                      <Chip
                        key={idx}
                        icon={<AlternateEmail />}
                        label={tag.patientName || tag.patientId}
                        size="small"
                        onClick={(e) => setStudyPopover(prev => prev?.studyId === tag.studyId ? null : { anchor: e.currentTarget, ...tag, orgId: access.org?.id })}
                        sx={{ 
                          cursor: 'pointer',
                          bgcolor: 'black',
                          color: 'white',
                          '& .MuiChip-icon': {
                            color: 'white'
                          }
                        }}
                      />
                    ))}
                  </Box>
                )}
                {msg.transcript && (
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      icon={<Description />}
                      label="Video Call Transcript"
                      size="small"
                      onClick={() => setTranscriptDialog(msg.transcript)}
                      sx={{ 
                        cursor: 'pointer',
                        bgcolor: 'black',
                        color: 'white',
                        '& .MuiChip-icon': {
                          color: 'white'
                        }
                      }}
                    />
                  </Box>
                )}
                {msg.reactions?.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                    {Object.entries(msg.reactions.reduce((acc, r) => {
                      if (!acc[r.emoji]) acc[r.emoji] = { count: 0, users: [] };
                      acc[r.emoji].count++;
                      acc[r.emoji].users.push(r.userId);
                      return acc;
                    }, {})).map(([emoji, data]) => {
                      const hasReacted = data.users.includes(user?.id);
                      return (
                        <Chip
                          key={emoji}
                          label={`${emoji} ${data.count}`}
                          size="small"
                          onClick={() => hasReacted ? handleRemoveReaction(msg.id, emoji) : handleReaction(msg.id, emoji)}
                          sx={{ 
                            cursor: 'pointer', 
                            fontSize: '0.7rem',
                            bgcolor: hasReacted ? 'primary.light' : 'transparent',
                            color: hasReacted ? 'primary.contrastText' : 'inherit',
                            '&:hover': { bgcolor: hasReacted ? 'primary.main' : 'action.hover' }
                          }}
                        />
                      );
                    })}
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.7rem' }}>
                    {new Date(msg.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    {msg.edited && ' (edited)'}
                  </Typography>
                  {!isOwn && (
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        setSelectedMessage(msg);
                        setReactionMenuAnchor(e.currentTarget);
                      }}
                      sx={{ fontSize: '1rem', p: 0.5, mt: -0.4 }}
                    >
                      ☺
                    </IconButton>
                  )}
                  {isOwn && (
                    <Tooltip title={msg.readBy?.length > 0 ? `Read by: ${msg.readBy.map(r => r.name).join(', ')}` : 'Delivered'}>
                      <DoneAll sx={{ 
                        fontSize: '1rem', 
                        opacity: 1,
                        fontWeight: 'bold',
                        color: msg.readBy?.length > 0 ? 'secondary.main' : 'inherit'
                      }} />
                    </Tooltip>
                  )}
                </Box>
              </Paper>
            </Box>
            </Box>
          );
        })}
        <div ref={messagesEndRef} />
      </Box>

      {typingUsers.length > 0 && (
        <Box sx={{ px: 2, py: 0.5, bgcolor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {typingUsers.map(u => u.name).join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
          </Typography>
        </Box>
      )}

      <Box sx={{ 
        p: { xs: 1, sm: 2 },
        bgcolor: 'background.paper', 
        borderTop: 1, 
        borderColor: 'divider', 
        position: 'relative',
        flexShrink: 0
      }}>
        {replyTo && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, mb: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Reply fontSize="small" />
            <Typography variant="body2" sx={{ flex: 1 }}>Replying to {replyTo.senderName}</Typography>
            <IconButton size="small" onClick={() => setReplyTo(null)}>
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        )}
        {editingMessage && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, mb: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Edit fontSize="small" />
            <Typography variant="body2" sx={{ flex: 1 }}>Editing message</Typography>
            <IconButton size="small" onClick={() => { setEditingMessage(null); setNewMessage(''); }}>
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        )}
        {attachments.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
            {attachments.map((file, idx) => (
              <Chip
                key={idx}
                icon={file.type?.startsWith('image/') ? <Image /> : <Description />}
                label={file.name}
                onDelete={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                size="small"
              />
            ))}
          </Box>
        )}
        {selectedStudies.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
            {selectedStudies.map((study, idx) => (
              <Chip
                key={idx}
                icon={<AlternateEmail />}
                label={study.PatientMainDicomTags?.PatientName || study.PatientMainDicomTags?.PatientID}
                onDelete={() => setSelectedStudies(prev => prev.filter((_, i) => i !== idx))}
                size="small"
              />
            ))}
          </Box>
        )}
        {studySearch.length > 2 && (studySearchLoading || studyOptions.length > 0) && (
          <Paper sx={{ position: 'absolute', bottom: '100%', left: 0, right: 0, mb: 1, maxHeight: 200, overflow: 'auto', zIndex: 1000 }}>
            {studySearchLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              studyOptions.map((study, idx) => (
                <MenuItem
                  key={idx}
                  onClick={() => {
                    setSelectedStudies(prev => [...prev, study]);
                    setStudySearch('');
                    setNewMessage(newMessage.replace(/@[\w\s]*$/, ''));
                  }}
                >
                  <AlternateEmail sx={{ mr: 1 }} />
                  {study.PatientMainDicomTags?.PatientName || 'Unknown'} ({study.PatientMainDicomTags?.PatientID || 'N/A'})
                </MenuItem>
              ))
            )}
          </Paper>
        )}
        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'flex-end' }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            multiple
          />
          <VideoCall 
            socket={require('../../../../api').socket}
            conversationId={conversationId}
            conversation={conversation}
            user={user}
            onTranscriptReady={(transcript) => {
              // Send transcript as a message with transcript flag
              sendMessage(conversationId, '', [], [], transcript).catch(console.error);
            }}
          />
          <IconButton 
            onClick={() => fileInputRef.current?.click()} 
            size="small"
          >
            <AttachFile />
          </IconButton>

          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={newMessage}
            onChange={(e) => {
              const val = e.target.value;
              const prevVal = newMessage;
              drafts[conversationId] = val;
              setNewMessage(val);
              const match = val.match(/@([^@\s][\w\s]*)$/);
              if (match) {
                setStudySearch(match[1].trim());
              } else {
                setStudySearch('');
              }
              if (val !== prevVal && val.length > prevVal.length) {
                handleTyping();
              }
            }}
            onKeyPress={handleKeyPress}
            placeholder={editingMessage ? "Edit message..." : "Type a message... (use @ to tag studies)"}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiInputBase-root': { fontSize: '16px' }
            }}
          />
          <IconButton 
            color="primary" 
            onClick={handleSend} 
            disabled={!newMessage.trim() && !attachments.length}
          >
            <Send />
          </IconButton>
        </Box>

        <Menu
          anchorEl={messageMenuAnchor}
          open={Boolean(messageMenuAnchor)}
          onClose={() => setMessageMenuAnchor(null)}
        >
          <MenuItem onClick={() => handleReply(selectedMessage)}>
            <Reply sx={{ mr: 1 }} /> Reply
          </MenuItem>

          {selectedMessage?.senderId === user?.id && (
            <>
              <MenuItem onClick={() => handleEditMessage(selectedMessage)}>
                <Edit sx={{ mr: 1 }} /> Edit
              </MenuItem>
              <MenuItem onClick={() => handleDeleteMessage(selectedMessage.id)}>
                <Delete sx={{ mr: 1 }} /> Delete
              </MenuItem>
            </>
          )}
        </Menu>

        <Menu
          anchorEl={reactionMenuAnchor}
          open={Boolean(reactionMenuAnchor)}
          onClose={() => setReactionMenuAnchor(null)}
          PaperProps={{
            sx: { 
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: 3,
              p: 1
            }
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            {REACTIONS.map(emoji => (
              <IconButton
                key={emoji}
                onClick={() => handleReaction(selectedMessage?.id, emoji)}
                sx={{ 
                  fontSize: '1.5rem',
                  '&:hover': { transform: 'scale(1.2)' },
                  transition: 'transform 0.1s'
                }}
              >
                {emoji}
              </IconButton>
            ))}
          </Box>
        </Menu>

      </Box>

      <StudyTagPopover
        anchorEl={studyPopover?.anchor}
        onClose={() => setStudyPopover(null)}
        studyId={studyPopover?.studyId}
        patientId={studyPopover?.patientId}
        orgId={studyPopover?.orgId}
      />

      <Dialog open={renameDialog} onClose={() => setRenameDialog(false)}>
        <DialogTitle>Rename Conversation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Group Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleRename()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialog(false)}>Cancel</Button>
          <Button onClick={handleRename} variant="contained">Rename</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addMembersDialog} onClose={() => setAddMembersDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Members</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {availableUsers.map(user => (
              <Box key={user.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <input
                  type="checkbox"
                  checked={selectedUsers.some(u => u.id === user.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedUsers([...selectedUsers, user]);
                    } else {
                      setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
                    }
                  }}
                />
                <Typography sx={{ ml: 1 }}>{user.name}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMembersDialog(false)}>Cancel</Button>
          <Button onClick={handleAddMembers} variant="contained" disabled={selectedUsers.length === 0}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(transcriptDialog)} onClose={() => setTranscriptDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Video Call Transcript</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
            {transcriptDialog}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            if (navigator.clipboard && navigator.clipboard.writeText) {
              navigator.clipboard.writeText(transcriptDialog).catch(err => {
                console.error('Failed to copy to clipboard:', err);
                alert('Failed to copy to clipboard. Please select and copy manually.');
              });
            } else {
              alert('Clipboard not supported. Please select and copy manually.');
            }
          }}>Copy to Clipboard</Button>
          <Button onClick={() => setTranscriptDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}